import { useMemo, useState } from 'react';
import { ChevronRight, Edit, Plus, Power, Search } from 'lucide-react';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { Button } from '../../../shared/components/Button';
import { Can } from '../../../shared/components/Can';
import { ConfirmDialog } from '../../../shared/components/ConfirmDialog';
import { EmptyState } from '../../../shared/components/EmptyState';
import { PageContainer } from '../../../shared/components/PageContainer';
import { TextInput } from '../../../shared/components/TextInput';
import { useToast } from '../../../shared/components/Toast';
import { FEATURES } from '../../../shared/lib/permissions';
import { OrgUnit, OrgUnitNode, mensagemDeErro } from '../api';
import { AreaFormModal } from '../components/AreaFormModal';
import { SituacaoBadge } from '../components/SituacaoBadge';
import { useArvoreAreas, useSituacaoArea } from '../hooks';

type StatusFilter = 'active' | 'inactive' | 'all';

function normalize(text: string) {
  return text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

/**
 * Filtra a árvore mantendo os ancestrais das áreas que casam com a busca
 * e a situação, para a hierarquia continuar legível.
 */
function filterTree(nodes: OrgUnitNode[], term: string, status: StatusFilter): OrgUnitNode[] {
  return nodes.flatMap((node) => {
    const children = filterTree(node.children, term, status);
    const matchesStatus = status === 'all' || (status === 'active' ? node.active : !node.active);
    const matchesTerm = !term || normalize(`${node.code} ${node.name}`).includes(term);
    if ((matchesStatus && matchesTerm) || children.length > 0) return [{ ...node, children }];
    return [];
  });
}

export default function AreasPage() {
  const toast = useToast();
  const arvore = useArvoreAreas();
  const situacao = useSituacaoArea();

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<StatusFilter>('active');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<OrgUnit | null>(null);
  const [toggling, setToggling] = useState<OrgUnit | null>(null);

  const tree = useMemo(() => arvore.data ?? [], [arvore.data]);
  const visible = useMemo(() => filterTree(tree, normalize(search.trim()), status), [tree, search, status]);

  function openForm(area: OrgUnit | null) {
    setEditing(area);
    setFormOpen(true);
  }

  async function confirmToggle() {
    if (!toggling) return;
    try {
      await situacao.mutateAsync({ id: toggling.id, active: !toggling.active });
      toast.success(toggling.active ? `Área "${toggling.name}" inativada.` : `Área "${toggling.name}" reativada.`);
    } catch (err) {
      toast.error(mensagemDeErro(err, 'Não foi possível alterar a situação da área.'));
    } finally {
      setToggling(null);
    }
  }

  function renderNode(node: OrgUnitNode, depth: number): React.ReactNode {
    return (
      <div key={node.id}>
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-2 hover:bg-gray-50">
          <div className="flex min-w-0 items-center gap-2" style={{ paddingLeft: depth * 24 }}>
            {depth > 0 && <ChevronRight className="h-4 w-4 shrink-0 text-gray-300" />}
            <span className="font-mono text-xs text-gray-500">{node.code}</span>
            <span className={`truncate text-sm ${node.active ? 'text-gray-900' : 'text-gray-400'}`}>{node.name}</span>
            <SituacaoBadge active={node.active} />
          </div>
          <Can feature={FEATURES.masterDataAreas} action="edit">
            <div className="flex shrink-0 gap-1">
              <button
                type="button"
                onClick={() => openForm(node)}
                className="rounded p-1.5 text-gray-500 hover:bg-gray-100 hover:text-blue-600"
                aria-label={`Editar ${node.name}`}
              >
                <Edit className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setToggling(node)}
                className="rounded p-1.5 text-gray-500 hover:bg-gray-100 hover:text-red-600"
                aria-label={node.active ? `Inativar ${node.name}` : `Reativar ${node.name}`}
              >
                <Power className="h-4 w-4" />
              </button>
            </div>
          </Can>
        </div>
        {node.children.map((child) => renderNode(child, depth + 1))}
      </div>
    );
  }

  return (
    <PageContainer
      title="Áreas organizacionais"
      subtitle="Estrutura de áreas e departamentos usada na lotação dos colaboradores."
      breadcrumbs={[{ label: 'Cadastros' }, { label: 'Áreas' }]}
      action={
        <Can feature={FEATURES.masterDataAreas} action="create">
          <Button onClick={() => openForm(null)}>
            <Plus className="mr-2 h-5 w-5" />
            Nova área
          </Button>
        </Can>
      }
    >
      <div className="flex flex-wrap items-end gap-4">
        <div className="w-full max-w-md">
          <TextInput
            label="Buscar por código ou nome"
            icon={<Search className="h-5 w-5" />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="min-w-[200px]">
          <AutocompleteSelect
            label="Situação"
            options={[
              { value: 'active', label: 'Ativas' },
              { value: 'inactive', label: 'Inativas' },
              { value: 'all', label: 'Todas' },
            ]}
            value={status}
            onChange={(v) => setStatus(v as StatusFilter)}
          />
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white">
        {arvore.isLoading ? (
          <p className="p-6 text-sm text-gray-500">Carregando áreas…</p>
        ) : arvore.isError ? (
          <p className="p-6 text-sm text-red-600">{mensagemDeErro(arvore.error, 'Não foi possível carregar as áreas.')}</p>
        ) : visible.length === 0 ? (
          <EmptyState title="Nenhuma área encontrada" description="Ajuste a busca ou cadastre uma nova área." />
        ) : (
          visible.map((node) => renderNode(node, 0))
        )}
      </div>

      <AreaFormModal
        open={formOpen}
        area={editing}
        tree={tree}
        onClose={() => setFormOpen(false)}
        onSaved={(message) => {
          setFormOpen(false);
          toast.success(message);
        }}
      />

      <ConfirmDialog
        open={toggling !== null}
        title={toggling?.active ? 'Inativar área?' : 'Reativar área?'}
        message={
          toggling?.active
            ? `A área "${toggling?.name}" deixará de aparecer para novas lotações. Áreas com subáreas ativas não podem ser inativadas.`
            : `A área "${toggling?.name}" voltará a ficar disponível.`
        }
        confirmLabel={toggling?.active ? 'Inativar' : 'Reativar'}
        danger={toggling?.active}
        busy={situacao.isPending}
        onConfirm={confirmToggle}
        onCancel={() => setToggling(null)}
      />
    </PageContainer>
  );
}
